import { useCallback, useState } from 'react'
import { FoldVertical, PencilLine, UnfoldVertical } from 'lucide-react'
import { loadUiSetting, saveUiSetting } from '@/lib/ui-settings'

/**
 * **Wie die Zeilen einer Erfassungsliste aufklappen** (Auftraggeber 10.10.2026) — in jeder Liste, deren
 * Zeilen weitere Felder unter der ersten Zeile tragen (Stückliste, Verbrauchsmaterial, Arbeitsschritte,
 * Verkaufspositionen, Kalkulationsblatt, Belegpositionen, Rechnungspositionen):
 *
 * - `auto` — beim Bearbeiten: Die Zeile klappt auf, solange sie den Fokus hat (das bisherige Verhalten).
 * - `auf` — immer aufgeklappt: Alle Zeilen zeigen ihre weiteren Felder.
 * - `zu` — immer zugeklappt: Keine Zeile klappt auf, auch nicht beim Bearbeiten.
 *
 * Gemerkt je Liste und Benutzer (`rowfold_<liste>` in den UI-Einstellungen, AP-23) — die Wahl folgt dem
 * Benutzer auf jeden Rechner, einen Mandanten-Standard gibt es dafür nicht.
 */
export type KlappModus = 'auto' | 'auf' | 'zu'

const MODI: readonly KlappModus[] = ['auf', 'auto', 'zu']
const istModus = (w: unknown): w is KlappModus => MODI.includes(w as KlappModus)

/** Der Klappmodus einer Liste. `liste`: Kleinbuchstaben, Ziffern, Unterstrich (Schlüsselraum der UI-Einstellungen). */
export function useKlappModus(liste: string, vorgabe: KlappModus = 'auto'): [KlappModus, (modus: KlappModus) => void] {
  const schluessel = `rowfold_${liste}`
  const [modus, setModus] = useState<KlappModus>(() => {
    const gemerkt = loadUiSetting<{ mode?: unknown } | null>(schluessel, null)?.mode
    return istModus(gemerkt) ? gemerkt : vorgabe
  })
  const setze = useCallback(
    (neu: KlappModus) => {
      setModus(neu)
      saveUiSetting(schluessel, { mode: neu })
    },
    [schluessel],
  )
  return [modus, setze]
}

/** Zeigt eine Zeile ihre weiteren Felder? `aktiv`: Die Zeile wird bearbeitet bzw. ist von Hand aufgeklappt. */
export function zeileOffen(modus: KlappModus | undefined, aktiv: boolean): boolean {
  return modus === 'auf' ? true : modus === 'zu' ? false : aktiv
}

const ICON: Record<KlappModus, typeof PencilLine> = { auf: UnfoldVertical, auto: PencilLine, zu: FoldVertical }

/** Name (für Vorleser) und Erklärung (beim Überfahren) je Modus. */
export type KlappTexte = Record<KlappModus, { name: string; titel: string }> & { gruppe: string }

const TEXTE: KlappTexte = {
  gruppe: 'Zeilen aufklappen',
  auf: { name: 'Zeilen immer aufgeklappt', titel: 'Alle Zeilen immer aufgeklappt — die weiteren Felder jeder Zeile sind zu sehen' },
  auto: { name: 'Zeilen beim Bearbeiten aufklappen', titel: 'Eine Zeile klappt auf, solange sie bearbeitet wird' },
  zu: { name: 'Zeilen immer zugeklappt', titel: 'Alle Zeilen immer zugeklappt — auch beim Bearbeiten' },
}

/** Dieselben Beschriftungen aus der Übersetzung (`common.rowFold.*`) — für Masken, die ihre Texte über i18n führen. */
export function klappTexte(t: (schluessel: string) => string): KlappTexte {
  return {
    gruppe: t('common.rowFold.group'),
    auf: { name: t('common.rowFold.open'), titel: t('common.rowFold.openHint') },
    auto: { name: t('common.rowFold.auto'), titel: t('common.rowFold.autoHint') },
    zu: { name: t('common.rowFold.closed'), titel: t('common.rowFold.closedHint') },
  }
}

/**
 * Der Umschalter dazu: drei kleine Knöpfe nebeneinander, der gewählte hervorgehoben.
 *
 * `texte` ersetzt einzelne Beschriftungen — für Listen, deren Zeilen nicht beim Bearbeiten, sondern je Zeile
 * von Hand aufklappen (Belegpositionen, Verkaufspositionen des Projekts), und für übersetzte Masken.
 */
export function KlappUmschalter({
  modus,
  onModus,
  texte,
  className = '',
}: {
  modus: KlappModus
  onModus: (modus: KlappModus) => void
  texte?: Partial<KlappTexte>
  className?: string
}) {
  const beschriftung = { ...TEXTE, ...texte }
  return (
    <span role="radiogroup" aria-label={beschriftung.gruppe} className={`inline-flex shrink-0 items-stretch overflow-hidden rounded border border-border ${className}`} data-auswahl-behalten data-klappmodus={modus}>
      {MODI.map((m) => {
        const Icon = ICON[m]
        const { name, titel } = beschriftung[m]
        const gewaehlt = m === modus
        return (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={gewaehlt}
            aria-label={name}
            title={titel}
            className={`inline-flex items-center px-1.5 py-1 ${m === 'auf' ? '' : 'border-l border-border'} ${gewaehlt ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            // Der Druck nimmt einer bearbeiteten Zeile den Fokus nicht — sie spränge sonst zu, ehe der Klick wirkt.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onModus(m)}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
          </button>
        )
      })}
    </span>
  )
}
